import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy, Knot" },
      { name: "description", content: "How Knot handles your journal entries, anonymous account, and AI processing." },
      { property: "og:title", content: "Privacy Policy, Knot" },
      { property: "og:description", content: "How Knot handles your journal entries, anonymous account, and AI processing." },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="min-h-dvh" style={{ background: "#EFE7DB" }}>
      <div className="mx-auto max-w-2xl px-5 py-10 pb-[max(2.5rem,env(safe-area-inset-bottom))]">
        <Link to="/" className="text-sm font-semibold" style={{ color: "#7A4FE0" }}>
          ← Back to Knot
        </Link>
        <div
          className="mt-4 rounded-3xl p-6 sm:p-8"
          style={{ background: "#FBF7F0", border: "1px solid #E6DDCB" }}
        >
          <div className="text-[11px] font-extrabold tracking-[0.18em] uppercase" style={{ color: "#7A4FE0" }}>
            Privacy
          </div>
          <h1 className="mt-2 text-3xl font-extrabold leading-tight text-[#2E2438]">
            Privacy Policy
          </h1>
          <p className="mt-1 text-xs text-[#6A5F7A]">Last updated: July 18, 2026</p>

          <div className="mt-6 space-y-6 text-[15px] leading-relaxed text-[#4E445A]">
            <section>
              <h2 className="text-lg font-extrabold text-[#2E2438]">The short version</h2>
              <p className="mt-2">
                Knot is a private CBT reframing journal. You don't create an account with a name or
                email. Your journal entries and conversations are stored in your own anonymous
                account so they follow you across sessions on the same device. When you write a
                worried thought, we send that text to Google Gemini (via the Lovable AI Gateway) to
                spot distortions and reply.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-extrabold text-[#2E2438]">What we store</h2>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>An anonymous account id created on your device the first time you open Knot.</li>
                <li>The profile you set up during onboarding (chosen name, themes, reminder preference).</li>
                <li>The entries you write, the distortions Knot spotted, your conversation with Knot, and your rewritten thought.</li>
                <li>A local flag remembering that you accepted the AI notice.</li>
              </ul>
              <p className="mt-2">
                We do not collect your real name, email, phone number, contacts, location, or device
                identifiers.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-extrabold text-[#2E2438]">AI processing</h2>
              <p className="mt-2">
                To detect cognitive distortions and reply during a session, Knot sends the text you
                write to Google Gemini through the Lovable AI Gateway. Only the words you write
                during that session are sent, never your account id or profile info. The provider
                processes your text to generate a response and does not use it to train models.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-extrabold text-[#2E2438]">Where it's stored</h2>
              <p className="mt-2">
                Entries and profile data are stored on Lovable Cloud (our managed backend) and are
                only readable by your anonymous account. Because there is no email or password, the
                only way to reach your data is from a device signed in to that anonymous account.
                Clearing your browser storage or reinstalling the app will sign you out of the
                anonymous account, and previous entries will no longer be reachable from the new
                session.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-extrabold text-[#2E2438]">Your choices</h2>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>You can decline the AI notice, in which case Knot won't send anything to the AI provider.</li>
                <li>You can stop using Knot at any time; nothing you write is required.</li>
                <li>To delete your data, contact us at the email below and we'll remove it.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-extrabold text-[#2E2438]">Not medical advice</h2>
              <p className="mt-2">
                Knot is a self-guided journaling tool inspired by David Burns' Feeling Good
                techniques. It is not therapy, not a medical device, and not a substitute for
                professional care. If you're in crisis, please contact the 988 Suicide & Crisis
                Lifeline (US) or your local emergency services.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-extrabold text-[#2E2438]">Contact</h2>
              <p className="mt-2">
                Questions or deletion requests: <a className="font-semibold" style={{ color: "#7A4FE0" }} href="mailto:umer.w.iqbal@gmail.com">umer.w.iqbal@gmail.com</a>.
              </p>
            </section>
          </div>

          <div className="mt-8 flex flex-wrap gap-3 text-sm">
            <Link to="/support" className="font-semibold" style={{ color: "#7A4FE0" }}>Support →</Link>
            <Link to="/" className="font-semibold" style={{ color: "#7A4FE0" }}>Back to Knot →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
